<template>
  <div class="rfi-div bg-dark-3 static-rfi">
    <form id="hero-rfi-asuo-static" data-cy="rfi-form" @submit.prevent="handleSubmit">
      <div class="rfi-content text-white pt-space-sm px-space-md pb-space-md pe-lg-space-xl">
        <!-- form fields -->
        <div id="hero-rfi-form-fields">
          <p class="fs-small text-secondary fw-bold mb-space-xxs">Step {{ currentStep }} of 2</p>
          <h2 class="h2-small mb-space-xxs">Request Information</h2>
          <!-- step 1 -->
          <div v-show="currentStep === 1" id="hero-rfi-step-1-form-fields">
            <p class="fs-xs mb-space-sm">
              To learn more about ASU Online or a specific program, fill out the form below and
              check your email for information on next steps. * Indicates a required field
            </p>
            <!-- degree type -->
            <div id="hero-rfi-degree-type-group" class="fw-bold mb-space-sm">
              <label
                id="hero-rfi-degree-type-label"
                for="hero-rfi-degree-type_typeahead__search"
                class="mb-space-xxs fs-xs"
                ><span aria-hidden="true">*</span> Degree type</label
              >

              <TypeAheadSelect
                id="hero-rfi-degree-type"
                v-model="form.degreeType as OptionType"
                label-id="hero-rfi-degree-type-label"
                preselect-opacity=".75"
                :options="degreeTypeOptions"
                placeholder="Select a degree type"
                data-cy="rfi-degree-type-filter"
                class="fw-normal text-dark-1"
                action-varint="light-5"
                @changed="handleDegreeTypeChange"
              >
              </TypeAheadSelect>
            </div>

            <!-- interest areas -->
            <div v-if="!isStudyPage" id="hero-rfi-interest-area-group" class="fw-bold mb-space-sm">
              <label
                id="hero-rfi-interest-area-label"
                for="hero-rfi-interest-area_typeahead__search"
                class="mb-space-xxs fs-xs"
                >Area of interest</label
              >

              <TypeAheadSelect
                id="hero-rfi-interest-area"
                v-model="form.interestArea as OptionType"
                preselect-opacity=".75"
                :options="interestAreaOptions"
                :disabled="interestAreaDisabled"
                placeholder="Select an interest area"
                data-cy="rfi-interest-area-filter"
                class="fw-normal text-dark-1"
                action-varint="light-5"
                label-id="hero-rfi-interest-area-label"
                @changed="handleInterestAreaChange"
              >
              </TypeAheadSelect>
            </div>

            <!-- programs -->
            <div id="hero-rfi-programs-group" class="fw-bold position-relative mb-0 pb-space-sm">
              <label
                id="hero-rfi-program-label"
                for="hero-rfi-program_typeahead__search"
                class="mb-space-xxs fs-xs"
              >
                <span v-if="programIsRequired" aria-hidden="true">* </span>Program
              </label>

              <TypeAheadSelect
                id="hero-rfi-program"
                v-model="form.program as OptionType"
                label-id="hero-rfi-program-label"
                preselect-opacity=".75"
                :options="programOptions"
                :disabled="programDisabled"
                placeholder="Select a program"
                data-cy="rfi-program-filter"
                class="fw-normal text-dark-1"
                action-varint="light-5"
                @changed="handleProgramChange"
              >
              </TypeAheadSelect>
            </div>

            <!-- military radio group -->
            <div
              id="military-group-hero"
              role="radiogroup"
              aria-labelledby="hero-military-group-label"
              class="form-check ps-0 position-relative mb-0 pb-space-sm d-flex flex-wrap"
            >
              <span id="hero-military-group-label" class="fw-bold form-check-label fs-xs w-100">
                * Have you served in the U.S. military or are you a military dependent?
              </span>
              <div v-for="option in militaryOptions" :key="option.label" class="form-check">
                <label class="form-check-label">
                  <input
                    v-model="form.isMilitary"
                    name="military"
                    class="form-check-input"
                    type="radio"
                    :value="option.value"
                    @change="handleMilitaryClick()"
                  />
                  {{ option.label }}
                </label>
              </div>
            </div>
          </div>

          <!-- step 2 -->
          <div v-show="currentStep === 2" id="hero-rfi-step-2-form-fields">
            <p class="fs-xs mb-space-sm">* Indicates a required field</p>
            <div class="row">
              <!-- first-name input field -->
              <div
                id="hero-rfi-first-name-group"
                class="col-12 col-lg-6 position-relative mb-0 pb-space-sm d-flex flex-column"
              >
                <label
                  id="hero-rfi-first-name-label"
                  for="hero-rfi-first-name"
                  class="fw-bold mb-space-xxxs fs-xs"
                  ><span aria-hidden="true">*</span> First name</label
                >
                <input
                  id="hero-rfi-first-name"
                  ref="firstNameRef"
                  v-model.trim="form.firstName"
                  type="text"
                  aria-labelledby="hero-rfi-first-name-label"
                  placeholder="Enter your first name"
                  data-cy="rfi-first-name"
                  class="form-control rounded-0"
                  :class="{
                    'is-invalid': firstNameErrorMessage
                  }"
                  :aria-invalid="firstNameErrorMessage ? 'true' : undefined"
                  :aria-describedby="
                    firstNameErrorMessage ? 'static-hero-rfi-first-name-error' : undefined
                  "
                  :aria-errormessage="
                    firstNameErrorMessage ? 'static-hero-rfi-first-name-error' : undefined
                  "
                  @blur="handleFirstNameFieldBlur"
                />
                <div
                  v-if="firstNameErrorMessage"
                  id="static-hero-rfi-first-name-error"
                  class="invalid-feedback"
                  role="alert"
                  aria-live="assertive"
                  aria-atomic="true"
                  tabindex="0"
                >
                  {{ firstNameErrorMessage }}
                </div>
              </div>
              <!-- last-name input field -->
              <div
                id="hero-rfi-last-name-group"
                class="position-relative mb-0 pb-space-sm col-12 col-lg-6 d-flex flex-column"
              >
                <label
                  id="hero-rfi-last-name-label"
                  for="hero-rfi-last-name"
                  class="fw-bold mb-space-xxxs fs-xs"
                  ><span aria-hidden="true">*</span> Last name</label
                >
                <input
                  id="hero-rfi-last-name"
                  v-model.trim="form.lastName"
                  type="text"
                  aria-labelledby="hero-rfi-last-name-label"
                  placeholder="Enter your last name"
                  data-cy="rfi-last-name"
                  class="form-control rounded-0"
                  :class="{
                    'is-invalid': lastNameErrorMessage
                  }"
                  :aria-invalid="lastNameErrorMessage ? 'true' : undefined"
                  :aria-describedby="
                    lastNameErrorMessage ? 'static-hero-rfi-last-name-error' : undefined
                  "
                  :aria-errormessage="
                    lastNameErrorMessage ? 'static-hero-rfi-last-name-error' : undefined
                  "
                  @blur="handleLastNameFieldBlur"
                />
                <div
                  v-if="lastNameErrorMessage"
                  id="static-hero-rfi-last-name-error"
                  class="invalid-feedback"
                  role="alert"
                  aria-live="assertive"
                  aria-atomic="true"
                  tabindex="0"
                >
                  {{ lastNameErrorMessage }}
                </div>
              </div>
            </div>

            <!-- email input field -->
            <div id="hero-rfi-email-group" class="position-relative mb-0 pb-space-sm">
              <label
                id="hero-rfi-email-label"
                for="hero-rfi-email"
                class="fw-bold mb-space-xxs fs-xs"
                ><span aria-hidden="true">*</span> Email address</label
              >
              <input
                id="hero-rfi-email"
                v-model.trim="form.email"
                type="email"
                aria-labelledby="hero-rfi-email-label"
                placeholder="Enter your email address"
                class="form-control rounded-0"
                :class="{
                  'is-invalid': emailErrorMessage
                }"
                :aria-invalid="emailErrorMessage ? 'true' : undefined"
                :aria-describedby="emailErrorMessage ? 'static-hero-rfi-email-error' : undefined"
                :aria-errormessage="emailErrorMessage ? 'static-hero-rfi-email-error' : undefined"
                data-cy="rfi-email"
                @input="handleEmailInput()"
                @blur="handleEmailFieldBlur"
              />
              <div
                v-if="emailErrorMessage"
                id="static-hero-rfi-email-error"
                class="invalid-feedback"
                role="alert"
                aria-live="assertive"
                aria-atomic="true"
                tabindex="0"
              >
                {{ emailErrorMessage }}
              </div>
            </div>

            <!-- phone number input field -->
            <div id="hero-rfi-phone-number-group" class="position-relative mb-0 pb-space-md">
              <label for="hero-rfi-asuonline" class="fw-bold mb-space-xxs fs-xs"
                ><span aria-hidden="true">*</span> Phone number</label
              >

              <PhoneInputRfi
                id="hero-rfi-asuonline"
                v-model.trim="form.phone.number"
                class="w-100"
                :class="{ 'is-invalid': displayedPhoneError }"
                :error="displayedPhoneError != ''"
                :no-validator-state="true"
                :aria-invalid="displayedPhoneError ? 'true' : undefined"
                :aria-describedby="
                  displayedPhoneError ? 'static-hero-rfi-phone-number-error' : undefined
                "
                :aria-errormessage="
                  displayedPhoneError ? 'static-hero-rfi-phone-number-error' : undefined
                "
                color="dark"
                @update="handlePhoneUpdate"
                @blur="handlePhoneFieldBlur"
              >
              </PhoneInputRfi>

              <div
                v-if="displayedPhoneError"
                id="static-hero-rfi-phone-number-error"
                ref="phoneErrorRef"
                class="invalid-feedback mb-space-xxs"
                role="alert"
                aria-live="assertive"
                aria-atomic="true"
                tabindex="0"
              >
                {{ displayedPhoneError }}
              </div>
            </div>

            <!-- consent block -->
            <div class="fs-xs mb-space-sm mb-md-space-md gdpr-links">
              <text-gdpr class="link-color" text-class="gdpr" theme="dark"></text-gdpr>
            </div>
            <input id="cq_req_id" type="hidden" name="cq_req_id" />
            <input id="cq_req_ip" type="hidden" name="cq_req_ip" />
          </div>
        </div>
        <!-- submit block -->
        <div class="row gx-0">
          <div class="col-12">
            <div class="d-flex align-items-center" :class="submitContainerClass">
              <button
                v-show="currentStep === 2"
                type="button"
                aria-label="go to previous step"
                class="text-white btn p-0"
                data-cy="rfi-previous-step-button"
                @click="previousStep"
              >
                <font-awesome-icon icon="arrow-left" size="1x" alt="back icon"></font-awesome-icon>
              </button>
              <button
                v-show="currentStep === 1"
                data-cy="rfi-continue-button"
                :disabled="!stepOneIsValid"
                type="button"
                class="fw-bold py-space-xxs px-space-sm btn btn-secondary outline-secondary"
                @click="nextStep"
              >
                Continue
              </button>
              <button
                v-show="currentStep === 2"
                class="fw-bold py-space-xxs px-space-sm btn btn-secondary outline-secondary"
                type="submit"
                variant="secondary"
                :disabled="submitDisabled"
                data-cy="rfi-submit-button"
              >
                Submit
                <div v-if="showLoader" class="d-inline">
                  <span
                    class="spinner-grow spinner-grow-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  <span class="sr-only">Loading...</span>
                  <span
                    class="spinner-grow spinner-grow-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  <span class="sr-only">Loading...</span>
                  <span
                    class="spinner-grow spinner-grow-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  <span class="sr-only">Loading...</span>
                </div>
              </button>
              <div
                v-if="submissionError"
                class="invalid-feedback d-block mt-space-xxs"
                role="alert"
                aria-live="assertive"
                aria-atomic="true"
                tabindex="0"
              >
                {{ submissionError }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script lang="ts" setup>
import TextGdpr from "~/components/rfi/TextGdpr.vue";
import TypeAheadSelect from "../TypeAheadSelect.vue";
import PhoneInputRfi from "./PhoneInputRfi.vue";

import { analyticsComposable } from "@rds-vue-ui/analytics-gs-composable";
import type { DegreeSeekingProgram } from "~/server/resolvers/resolvers-types";

interface OptionType {
  text: string;
  value: string;
}

interface Props {
  isStudyPage?: boolean;
  programList?: DegreeSeekingProgram[];
}

const props = withDefaults(defineProps<Props>(), {
  isStudyPage: false
});

const {
  form,
  lastNameErrorMessage,
  emailErrorMessage,
  phoneApiError,
  submissionError,
  firstNameErrorMessage,
  stepOneIsValid,
  programIsRequired,
  showLoader,
  submitDisabled,
  currentProgramCode,
  currentProgramName,
  degreeTypeOptions,
  programOptions,
  interestAreaOptions,
  interestAreaDisabled,
  programDisabled,
  handleDegreeTypeChange,
  handleEmailBlur: trackEmailBlur,
  handleEmailInput,
  handleFirstNameBlur: trackFirstNameBlur,
  handleInterestAreaChange,
  handleLastNameBlur: trackLastNameBlur,
  handleMilitaryClick,
  handlePhoneBlur: trackPhoneBlur,
  handlePhoneUpdate: updatePhoneState,
  handleProgramChange,
  syncResolvedStepOneSelection,
  handleSubmit
} = useRfi({
  isStudyPage: props.isStudyPage,
  programList: props.programList,
  enableUndecidedFlow: true
});

const currentStep = ref(1);
const firstNameRef = ref<HTMLInputElement | null>(null);
const phoneTouched = ref(false);
const phoneBlurred = ref(false);
const phoneErrorRef = ref<HTMLElement | null>(null);

const displayedPhoneError = computed(() => {
  if (phoneApiError.value) {
    return phoneApiError.value;
  }

  if (phoneBlurred.value && !form.value.phone.number) {
    return "Please enter a valid phone number and try again.";
  }

  if (phoneTouched.value && form.value.phone.number && form.value.phone.isValid === false) {
    return "Please enter a valid phone number and try again.";
  }

  return "";
});

const militaryOptions = [
  { label: "Yes", value: true },
  { label: "No", value: false }
];

const submitContainerClass = computed(() => {
  return currentStep.value === 1 ? "justify-content-end" : "justify-content-between";
});

const previousStep = () => {
  currentStep.value = currentStep.value - 1;
  analyticsComposable.trackLinkEvent("internal link", "main content", "request info", "back arrow");
};

const handleFirstNameFieldBlur = () => {
  form.value.firstName = form.value.firstName ?? "";
  trackFirstNameBlur();
};

const handleLastNameFieldBlur = () => {
  form.value.lastName = form.value.lastName ?? "";
  trackLastNameBlur();
};

const handleEmailFieldBlur = () => {
  form.value.email = form.value.email ?? "";
  trackEmailBlur();
};

const handlePhoneFieldBlur = () => {
  phoneTouched.value = true;
  phoneBlurred.value = true;
  trackPhoneBlur();
  window.setTimeout(() => {
    if (displayedPhoneError.value) {
      phoneErrorRef.value?.focus();
    }
  }, 0);
};

const handlePhoneUpdate = (payload: Parameters<typeof updatePhoneState>[0]) => {
  phoneTouched.value = phoneTouched.value || Boolean(payload.nationalNumber);
  phoneBlurred.value = false;
  updatePhoneState(payload);
};

const nextStep = () => {
  syncResolvedStepOneSelection();

  if (currentProgramName.value && currentProgramCode.value) {
    if (props.isStudyPage) {
      analyticsComposable.trackEvent({
        event: "form",
        name: "onclick",
        action: "click",
        type: "click",
        region: "main content",
        section: "request info",
        text: "continue",
        component: "rfi step 1 of 2",
        degree_type:
          form.value.degreeType && form.value.degreeType.value
            ? form.value.degreeType.value.toLowerCase()
            : "select a degree type",
        program: currentProgramName.value.toLowerCase(),
        program_code: currentProgramCode.value,
        military_service: form.value.isMilitary === false ? "no" : "yes"
      });
    } else {
      analyticsComposable.trackEvent({
        event: "form",
        name: "onclick",
        action: "click",
        type: "click",
        region: "main content",
        section: "request info",
        text: "continue",
        component: "rfi-hero",
        degree_type:
          form.value.degreeType && form.value.degreeType.value
            ? form.value.degreeType.value.toLowerCase()
            : "select a degree type",
        area_of_interest:
          form.value.interestArea && form.value.interestArea.value
            ? form.value.interestArea.value.toLowerCase()
            : "select an interest area",
        program: currentProgramName.value.toLowerCase(),
        program_code: currentProgramCode.value,
        military_service: form.value.isMilitary === false ? "no" : "yes"
      });
    }
    currentStep.value = currentStep.value + 1;
    nextTick(() => {
      firstNameRef.value?.focus();
    });
  } else {
    console.error("missing program name or code, when proceeding to next step");
  }
};
</script>

<style lang="scss" scoped>
:deep() {
  .m-phone-number-input button {
    background-color: transparent;
  }
  .m-phone-number-input {
    --maz-color-bg: var(--rds-dark-3);
    --maz-color-muted: rgba(255, 255, 255, 0.75);
    --maz-color-text: var(--rds-white);
    --maz-border-radius: 0;
  }
  .m-select .m-select-list-item.--is-selected {
    color: var(--rds-dark-3) !important;
    background-color: var(--rds-white) !important;
  }
  .m-select .m-select-list-item {
    color: var(--rds-white) !important;
  }
  .m-select .m-select-list-item:hover {
    color: var(--rds-dark-3) !important;
  }
  .m-input-wrapper {
    border-top: none;
    border-left: none;
    border-right: none;
    border-radius: 0;
    --maz-color-primary: var(--rds-secondary);
  }
}

.form-check-input:checked {
  color: var(--rds-dark-3);
  --rds-form-check-bg-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='%23000' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='m6 10 3 3 6-6'/%3e%3c/svg%3e");
  --rds-form-check-bg: var(--rds-secondary);
  background-color: var(--rds-secondary);
  border-color: var(--rds-secondary);
}
.form-check {
  gap: 1rem;
}
.form-check-input:focus {
  outline: 2px solid var(--rds-secondary);
  outline-offset: 2px;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}
// form input css
.form-control {
  border-top: none;
  border-left: none;
  border-right: none;
  padding-top: 0px;
  padding-left: 0px;
  background-color: var(--rds-dark-3, #191919);
  color: #fafafa;
  border-bottom-right-radius: 0;
  border-bottom-left-radius: 0;
}
.form-control:focus {
  color: #ffc627;
  border-color: #ffc627;
  box-shadow: none;
  outline: none;
}

// Typeahead css
:deep(div.typeahead__dropdown-toggle) {
  background-color: var(--rds-dark-3, #191919);
  color: white;
  border-top-color: transparent;
  border-left-color: transparent;
  border-right-color: transparent;
  border-radius: 0;
}
:deep(div.typeahead__action) {
  color: white;
}
:deep(.link-color a) {
  color: #ffc627;
}
.rfi-content {
  width: 434px;
  height: 100%;
}
div.invalid-feedback {
  margin-top: 0 !important;
  color: #ff7b7d !important;
}

.outline-secondary:focus-visible {
  outline: 2px solid #ffc627;
}
</style>
